<template>
    <div>
        <table class="form-table">
            <tbody>
                <tr v-if="multipleViews">
                    <td>
                        <label>{{ $t('Use multiple Table Views') }}:</label>
                    </td>
                    <td>
                        <CicCheckbox
                            id="campaign_multiview"
                            :value="item.useMultipleViews"
                            @input="updateUseMultipleViews"
                        />
                    </td>
                </tr>
                <tr v-if="!item.useMultipleViews">
                    <td>
                        <label>{{ $t('Table View') }}:</label>
                    </td>
                    <td>
                        <CicSelect
                            id="campaign_view"
                            :options="
                                views
                                    .filter((view) => !view.deleted || view.viewId === item.viewId)
                                    .map((view) => ({ value: view.viewId, label: view.name }))
                            "
                            :placeholder="$t('Please select...')"
                            :value="item.viewId"
                            clearable
                            @input="(val) => updateFieldValue('viewId', val)"
                        />
                    </td>
                </tr>
                <tr v-if="multipleViews && item.useMultipleViews && dynamicQueues">
                    <td>
                        <label>{{ $t('Override preview dial queue') }}:</label>
                    </td>
                    <td>
                        <CicCheckbox
                            id="campaign_overridequeue"
                            :value="item.useDynamicQueues"
                            @input="(val) => updateFieldValue('useDynamicQueues', val)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
        <div v-if="multipleViews && item.useMultipleViews" style="margin-left: 50px; margin-top: 3px">
            <ul class="table-view-rules">
                <li v-for="(rule, index) in item.viewRules">
                    <table>
                        <tbody>
                            <tr class="rule-label">
                                <td>
                                    <label>{{ $t('Priority [x]').replace(/\[x]/g, index + 1) }}</label>
                                    <IconButton
                                        v-if="item.viewRules.length > 1"
                                        icon="remove-icon"
                                        @click="removeRule(index)"
                                    />
                                    <IconButton
                                        v-if="index === item.viewRules.length - 1"
                                        icon="add-icon"
                                        @click="addRule"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t('Weight table views equally') }}:</td>
                                <td>
                                    <CicCheckbox
                                        :value="rule.weightedEqually"
                                        @input="(val) => updateRule(index, 'weightedEqually', val)"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style="text-align: center">{{ $t('Table Views') }}</td>
                                <td>
                                    <IconButton
                                        v-if="index > 0"
                                        icon="up-icon"
                                        class="el-icon-arrow-up"
                                        @click="moveUp(index)"
                                    />
                                </td>
                                <td v-if="!rule.weightedEqually" class="viewweighting" style="text-align: center">%</td>
                                <td
                                    v-if="dynamicQueues && item.useDynamicQueues"
                                    class="dynamicqueue"
                                    style="text-align: center"
                                >
                                    {{ $t('Queue') }}
                                </td>
                                <td></td>
                            </tr>
                            <tr v-for="(ruleView, viewIndex) in rule.views">
                                <td>
                                    <CicSelect
                                        :options="
                                            views
                                                .filter((view) => !view.deleted || view.viewId === ruleView.viewId)
                                                .map((view) => ({ value: view.viewId, label: view.name }))
                                        "
                                        :placeholder="$t('Please select...')"
                                        :value="ruleView.viewId"
                                        clearable
                                        @input="(val) => updateRuleView(index, viewIndex, 'viewId', val)"
                                    />
                                </td>
                                <td></td>
                                <td v-if="!rule.weightedEqually">
                                    <CicTextbox
                                        :value="ruleView.percentage"
                                        maxlength="3"
                                        int-only
                                        @input="(val) => updateRuleView(index, viewIndex, 'percentage', val)"
                                    />
                                </td>
                                <td v-if="dynamicQueues && item.useDynamicQueues" class="dynamicqueue">
                                    <CicSelect
                                        :options="
                                            queues
                                                .filter((queue) => queue.allowed || queue.queueId === ruleView.queueId)
                                                .map((queue) => ({ value: queue.queueId, label: queue.name }))
                                        "
                                        :placeholder="$t('Please select...')"
                                        :value="ruleView.queueId"
                                        clearable
                                        @input="(val) => updateRuleView(index, viewIndex, 'queueId', val)"
                                    />
                                </td>
                                <td style="padding: 0">
                                    <IconButton
                                        v-if="rule.views.length > 1"
                                        icon="remove-icon"
                                        @click="removeRuleView(index, viewIndex)"
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td
                                    :colspan="
                                        2 +
                                        (rule.weightedEqually ? 0 : 1) +
                                        (dynamicQueues && item.useDynamicQueues ? 1 : 0)
                                    "
                                ></td>
                                <td style="padding: 0">
                                    <IconButton icon="add-icon" @click="addRuleView(index)" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import IconButton from '@/components/controls/IconButton';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditTableViews',
        components: {
            CicTextbox,
            CicCheckbox,
            CicSelect,
            IconButton,
        },
        props: {
            dynamicQueues: {
                type: Boolean,
                default: false,
            },
            item: {
                type: Object,
                required: true,
            },
            multipleViews: {
                type: Boolean,
                default: false,
            },
            queues: {
                type: Array,
                required: true,
            },
            views: {
                type: Array,
                required: true,
            },
        },
        emits: ['change', 'changeView'],
        methods: {
            loadViewColumns(name, value) {
                if (name === 'viewId' && value > 0) {
                    const view = this.views.find((v) => v.viewId === value);
                    if (view && !view.columns) {
                        this.$emit('changeView', value);
                    }
                }
            },
            moveUp(index) {
                const rules = JSON.parse(JSON.stringify(this.item.viewRules));
                rules.splice(index - 1, 0, rules.splice(index, 1)[0]);
                this.updateFieldValue('viewRules', rules);
            },
            updateFieldValue(name, value) {
                this.$emit('change', name, value);
                this.loadViewColumns(name, value);
            },
            addRule() {
                const rules = JSON.parse(JSON.stringify(this.item.viewRules));
                rules.push({
                    weightedEqually: false,
                    views: [
                        {
                            viewId: null,
                            queueId: null,
                            percentage: '',
                            cliType: 0,
                            cliProfileId: null,
                            cliColumn: null,
                            timesType: 0,
                            timesProfileId: null,
                            timesColumn: null,
                        },
                    ],
                });
                this.updateFieldValue('viewRules', rules);
            },
            removeRule(index) {
                const rules = JSON.parse(JSON.stringify(this.item.viewRules));
                rules.splice(index, 1);
                this.updateFieldValue('viewRules', rules);
            },
            updateRule(index, name, value) {
                const rules = JSON.parse(JSON.stringify(this.item.viewRules));
                rules[index][name] = value;
                this.updateFieldValue('viewRules', rules);
            },
            addRuleView(index) {
                const views = JSON.parse(JSON.stringify(this.item.viewRules[index].views));
                views.push({
                    viewId: null,
                    queueId: null,
                    percentage: '',
                    cliType: 0,
                    cliProfileId: null,
                    cliColumn: null,
                    timesType: 0,
                    timesProfileId: null,
                    timesColumn: null,
                });
                this.updateRule(index, 'views', views);
            },
            removeRuleView(index, viewIndex) {
                const views = JSON.parse(JSON.stringify(this.item.viewRules[index].views));
                views.splice(viewIndex, 1);
                this.updateRule(index, 'views', views);
            },
            updateRuleView(index, viewIndex, name, value) {
                const views = JSON.parse(JSON.stringify(this.item.viewRules[index].views));
                views[viewIndex][name] = value;
                if (name === 'viewId') {
                    views[viewIndex].cliColumn = null;
                    views[viewIndex].timesColumn = null;
                }

                this.updateRule(index, 'views', views);
                this.loadViewColumns(name, value);
            },
            updateUseMultipleViews(val) {
                this.updateFieldValue('useMultipleViews', val);
                const rules = [];
                if (val) {
                    rules.push({
                        weightedEqually: false,
                        views: [
                            {
                                viewId: null,
                                queueId: null,
                                percentage: '',
                                cliType: 0,
                                cliProfileId: null,
                                cliColumn: null,
                                timesType: 0,
                                timesProfileId: null,
                                timesColumn: null,
                            },
                        ],
                    });
                }
                this.updateFieldValue('viewRules', rules);
            },
        },
    };
</script>
